import { Dtos } from "@inrev/common";
import {
	ACTIVE_AGENT_FOLLOWER_REQUIRED,
	isOnlySetupCompleteFollower,
} from "@portal/api/followers/hasActiveAgentFollower";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useId } from "react";
import { ConfirmationModalClickWrapper } from "../../layout/ConfirmationModalClickWrapper";
import { cn } from "../../lib/utils";
import { Button, Icon } from "../../primitives";
import { PortaledTooltip } from "../../ui/Tooltip";

const FOLLOW_LABEL = "Follow this account";
const FOLLOWING_LABEL = "Unfollow this account";

export const AgentFollowToggle = ({
	isFollowing,
	followers,
	onFollow,
	onUnfollow,
	trackingId,
	followLabel = FOLLOW_LABEL,
	unfollowLabel = FOLLOWING_LABEL,
}: {
	isFollowing: boolean | undefined;
	followers?: Dtos.Follower.Get.Response[];
	onFollow: () => void;
	onUnfollow: () => void;
	trackingId?: string;
	followLabel?: string;
	unfollowLabel?: string;
}) => {
	const tooltipId = useId();
	const { user } = useAuthenticatedUser();

	if (isFollowing === undefined || followers === undefined) {
		return (
			<div
				data-testid="agent-follow-toggle"
				className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-surface-neutral"
			/>
		);
	}

	const label = isFollowing ? unfollowLabel : followLabel;
	const button = (
		<Button
			variant="ghost"
			size="sm"
			aria-label={label}
			data-testid="agent-follow-toggle"
			data-tooltip-id={tooltipId}
			data-tooltip-content={label}
			trackingId={trackingId}
			onClick={() => (isFollowing ? onUnfollow() : onFollow())}
			className={cn(
				"h-7 w-7 shrink-0 rounded-full p-0",
				isFollowing
					? "bg-surface-neutral hover:border hover:bg-surface-danger hover:border-danger"
					: "border border-dashed border-bc-default hover:border-confirm hover:bg-surface-confirm",
			)}
		>
			<Icon
				type={isFollowing ? "user-check" : "user-plus"}
				size="micro"
				color="secondary"
				filled={isFollowing}
			/>
		</Button>
	);

	return (
		<>
			{isFollowing && isOnlySetupCompleteFollower(followers, user.id) ? (
				<ConfirmationModalClickWrapper
					message={ACTIVE_AGENT_FOLLOWER_REQUIRED}
					confirmButtonText="Okay"
					confirmationButtonColor="primary"
					cancelButton={false}
				>
					{button}
				</ConfirmationModalClickWrapper>
			) : (
				button
			)}
			<PortaledTooltip id={tooltipId} />
		</>
	);
};
